import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ColumnVisibilityToggle from '../../../../components/v2/ColumnVisibilityToggle.jsx';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import { exportEnvironmentalCsv } from '../../../../services/csvExport.js';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import NoneActionCell from '../../../../components/v2/NoneActionCell.jsx';
import Pagination from '../../../../components/v2/Pagination.jsx';
import { anchoredPopoverStyle } from '../../../../utils/popoverPosition.js';
import RowActionsMenu from '../../../../components/v2/RowActionsMenu.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

// ---------------------------------------------------------------------------
// Alarm type labels
// ---------------------------------------------------------------------------
const ALARM_TYPE_LABELS = { 1: 'Global', 2: 'Occupied', 3: 'Unoccupied', 4: 'Custom' };

// ---------------------------------------------------------------------------
// Column definitions per env type
// ---------------------------------------------------------------------------
const TEMP_COLUMN_DEFS = [
    { key: 'description', label: 'Description', sortable: true, filterable: false, required: true },
    { key: 'type_display', label: 'Type', sortable: true, filterable: true, required: false },
    { key: 'low_alarm', label: 'Low Temp (\u00B0F)', sortable: true, filterable: false, required: true },
    { key: 'high_alarm', label: 'High Temp (\u00B0F)', sortable: true, filterable: false, required: true },
    { key: 'alarm_display', label: 'Alarm On', sortable: true, filterable: true, required: false },
    { key: 'interval_display', label: 'Report Interval', sortable: true, filterable: false, required: false },
    { key: 'device_count', label: 'Devices', sortable: true, filterable: false, required: false },
];

const HUMIDITY_COLUMN_DEFS = [
    { key: 'description', label: 'Description', sortable: true, filterable: false, required: true },
    { key: 'type_display', label: 'Type', sortable: true, filterable: true, required: false },
    { key: 'low_alarm', label: 'Low Humidity (%)', sortable: true, filterable: false, required: true },
    { key: 'high_alarm', label: 'High Humidity (%)', sortable: true, filterable: false, required: true },
    { key: 'alarm_display', label: 'Alarm On', sortable: true, filterable: true, required: false },
    { key: 'interval_display', label: 'Report Interval', sortable: true, filterable: false, required: false },
    { key: 'device_count', label: 'Devices', sortable: true, filterable: false, required: false },
];

const BATTERY_COLUMN_DEFS = [
    { key: 'description', label: 'Description', sortable: true, filterable: false, required: true },
    { key: 'type_display', label: 'Type', sortable: true, filterable: true, required: false },
    { key: 'low_alarm', label: 'Low Battery (V)', sortable: true, filterable: false, required: true },
    { key: 'alarm_display', label: 'Alarm On', sortable: true, filterable: true, required: false },
    { key: 'interval_display', label: 'Report Interval', sortable: true, filterable: false, required: false },
    { key: 'device_count', label: 'Devices', sortable: true, filterable: false, required: false },
];

const COLUMN_DEFS_MAP = {
    temperature: TEMP_COLUMN_DEFS,
    humidity: HUMIDITY_COLUMN_DEFS,
    battery: BATTERY_COLUMN_DEFS,
};

const ENV_TYPE_LABEL = {
    temperature: 'Temperature',
    humidity: 'Humidity',
    battery: 'Battery',
};

// ---------------------------------------------------------------------------
// Devices popover cell — badge with hover list
// ---------------------------------------------------------------------------
// When `onAssign` + `row` are provided the badge click opens the Assign Devices
// drawer (same target as the row kebab); the hover preview keeps working. With
// no devices, "None" keeps the muted look but carries the same action so the
// first device can be assigned from here.
//
// Only reached for custom rows — default rows render "All" instead, which is a
// statement about coverage rather than an empty assignment, so it stays inert.
const DevicesPopoverCell = ({ deviceObjects, onAssign, row }) => {
    const [open, setOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const ref = useRef(null);
    const hoverTimeout = useRef(null);
    const isClickToAssign = typeof onAssign === 'function';

    const computePosition = () => {
        const style = anchoredPopoverStyle(ref.current, { flipBelow: 200, gap: 4 });
        if (style) setPopoverStyle(style);
    };

    const handleMouseEnter = () => { clearTimeout(hoverTimeout.current); computePosition(); setOpen(true); };
    const handleMouseLeave = () => { hoverTimeout.current = setTimeout(() => setOpen(false), 100); };

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    useEffect(() => () => clearTimeout(hoverTimeout.current), []);

    /* After the hooks, never before: assigning a first device flips this branch on a
       live row, and an early return above the effects changes the hook count. */
    if (!deviceObjects || deviceObjects.length === 0) {
        return (
            <NoneActionCell
                className="ap-cell-text--muted"
                onClick={isClickToAssign ? () => onAssign(row) : undefined}
                title="Click to assign devices"
            />
        );
    }

    return (
        <div className="ap-device-cell" ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <button
                type="button"
                className="ap-device-badge"
                onClick={() => {
                    if (isClickToAssign) {
                        setOpen(false);
                        onAssign(row);
                    } else {
                        computePosition();
                        setOpen(!open);
                    }
                }}
                title={isClickToAssign ? 'Click to assign devices' : undefined}
                aria-label={isClickToAssign
                    ? `Assign devices — ${deviceObjects.length} currently assigned`
                    : `${deviceObjects.length} device${deviceObjects.length !== 1 ? 's' : ''} assigned`}
            >
                <i className="fe fe-cpu" style={{ fontSize: '12px' }} />
                {deviceObjects.length}
            </button>
            {open ? (
                <div className="ap-device-popover" style={popoverStyle}>
                    <div className="ap-device-popover__title">Devices ({deviceObjects.length})</div>
                    <ul className="ap-device-popover__list">
                        {deviceObjects.map((d, i) => (
                            <li key={d.device_id || i} className="ap-device-popover__item">
                                <span className="ap-device-popover__name">{d.device_name || '—'}</span>
                                <span className="ap-device-popover__uuid">
                                    {[d.device_category, d.descriptions, d.manufacture_uuid, d.entity_name].filter(Boolean).join(' · ')}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Row-level kebab menu
// ---------------------------------------------------------------------------
const RowActions = ({ row, onEdit, onAssignDevices, onDelete }) => {
    const isCustom = row.environmental_alarm_type_id === 4;

    return (
        <RowActionsMenu label={`Actions for ${row.description}`} classPrefix="ap">
            {(close) => (
                <>
                    <button type="button" onClick={() => { onEdit(row); close(); }} className="ap-row-actions__item">
                        <i className="fe fe-edit ap-row-actions__item-icon" aria-hidden="true" />
                        Edit Settings
                    </button>
                    {isCustom ? (
                        <button type="button" onClick={() => { onAssignDevices(row); close(); }} className="ap-row-actions__item">
                            <i className="fe fe-cpu ap-row-actions__item-icon" aria-hidden="true" />
                            Assign Devices
                        </button>
                    ) : null}
                    <button type="button" onClick={() => { onDelete([row.control_id]); close(); }} className="ap-row-actions__item ap-row-actions__item--danger">
                        <i className="fe fe-trash-2 ap-row-actions__item-icon" aria-hidden="true" />
                        Delete
                    </button>
                </>
            )}
        </RowActionsMenu>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const EnvironmentalTable = ({ data, envType, devices, onAdd, onEdit, onDelete, onAssignDevices, onCreateDefaults, saving }) => {
    const COLUMN_DEFS = COLUMN_DEFS_MAP[envType] || TEMP_COLUMN_DEFS;
    const DEFAULT_VISIBLE = useMemo(() => COLUMN_DEFS.map((c) => c.key), [COLUMN_DEFS]);
    const ALL_COLUMN_KEYS = useMemo(() => COLUMN_DEFS.map((c) => c.key), [COLUMN_DEFS]);
    const COLUMN_LABELS = useMemo(() => {
        const labels = {};
        COLUMN_DEFS.forEach((col) => { labels[col.key] = col.label; });
        return labels;
    }, [COLUMN_DEFS]);

    const typeLabel = ENV_TYPE_LABEL[envType] || 'Environmental';

    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortColumn, setSortColumn] = useState('description');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({});
    const [visibleColumns, handleColumnToggle] = usePersistedColumns(`settings-environmental-${envType}`, DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    // Reset state when env type or data changes
    useEffect(() => { setSelectedIds([]); }, [data]);
    useEffect(() => {
        setSearch('');
        setFilters({});
        setSortColumn('description');
        setSortOrder('asc');
        setPage(1);
    }, [envType, COLUMN_DEFS]);

    // Enrich data with display values and resolve device objects
    const enrichedData = useMemo(() => {
        return (data || []).map((item) => {
            const deviceIds = item.device_ids ? item.device_ids.split(',').filter(Boolean) : [];
            const deviceObjects = deviceIds
                .map((id) => (devices || []).find((d) => String(d.device_id) === String(id)))
                .filter(Boolean);
            return {
                ...item,
                type_display: ALARM_TYPE_LABELS[item.environmental_alarm_type_id] || 'Unknown',
                alarm_display: item.alarm_on === 1 ? 'Yes' : 'No',
                interval_display: item.test_after && item.test_after >= 60
                    ? `${Math.round(item.test_after / 60)} min`
                    : `${item.test_after || 0} sec`,
                device_count: deviceObjects.length,
                _deviceObjects: deviceObjects,
                isDefault: item.environmental_alarm_type_id !== 4,
            };
        });
    }, [data, devices]);

    // Active columns based on visibility
    const activeColumns = useMemo(
        () => COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key)),
        [visibleColumns, COLUMN_DEFS]
    );

    // Filter values for filter popovers
    const getFilterValues = useCallback((columnKey) => {
        const values = new Set();
        enrichedData.forEach((row) => {
            const val = row[columnKey];
            values.add(val != null && val !== '' ? String(val) : '(empty)');
        });
        return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [enrichedData]);

    // ======================================================================
    // Data pipeline: Search → Filters → Sort
    // ======================================================================
    const filteredData = useMemo(() => {
        let d = [...enrichedData];

        // Search
        if (search.trim()) {
            const term = search.toLowerCase();
            d = d.filter((r) =>
                (r.description || '').toLowerCase().includes(term) ||
                (r.type_display || '').toLowerCase().includes(term) ||
                (r.interval_display || '').toLowerCase().includes(term)
            );
        }

        // Column filters
        Object.entries(filters).forEach(([columnKey, values]) => {
            if (!values || values.length === 0) return;
            d = d.filter((row) => {
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return values.includes(strVal);
            });
        });

        // Sort — use raw seconds for interval column
        d.sort((a, b) => {
            let aVal, bVal;
            if (sortColumn === 'interval_display') {
                aVal = a.test_after ?? 0;
                bVal = b.test_after ?? 0;
            } else {
                aVal = a[sortColumn] ?? '';
                bVal = b[sortColumn] ?? '';
            }
            const result = typeof aVal === 'number' && typeof bVal === 'number'
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
            return sortOrder === 'asc' ? result : -result;
        });

        return d;
    }, [enrichedData, search, filters, sortColumn, sortOrder]);

    // Reset page on filter/search change
    useEffect(() => { setPage(1); }, [search, filters, sortColumn, sortOrder]);

    // ======================================================================
    // Pagination
    // ======================================================================
    const totalFiltered = filteredData.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, totalFiltered);
    const paginatedData = filteredData.slice(startIdx, endIdx);

    // ======================================================================
    // Selection
    // ======================================================================
    const allSelected = paginatedData.length > 0 && paginatedData.every((r) => selectedIds.includes(r.control_id));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const handleSelectRow = useCallback((id) => {
        setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }, []);

    const handleSelectAll = useCallback(() => {
        const pageIds = paginatedData.map((r) => r.control_id);
        const currentAllSelected = pageIds.every((id) => selectedIds.includes(id));
        if (currentAllSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
        }
    }, [paginatedData, selectedIds]);

    // ======================================================================
    // Handlers
    // ======================================================================
    const handleSort = useCallback((column) => {
        if (sortColumn === column) {
            setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    }, [sortColumn]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: (prev[columnKey] || []).filter((v) => v !== value) }));
    }, []);

    const handleClearAllFilters = useCallback(() => { setFilters({}); setSearch(''); }, []);

    const handleExportAll = useCallback(() => {
        if (filteredData.length) exportEnvironmentalCsv(filteredData, envType);
    }, [filteredData, envType]);

    const handleBulkExport = useCallback(() => {
        const selected = enrichedData.filter((r) => selectedIds.includes(r.control_id));
        if (selected.length) exportEnvironmentalCsv(selected, envType, `${envType}-settings-selected.csv`);
    }, [selectedIds, enrichedData, envType]);

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        onDelete(selectedIds);
    };

    // ======================================================================
    // Sort icons
    // ======================================================================
    const renderSortIcon = (columnKey) => {
        if (sortColumn !== columnKey) {
            return (
                <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--neutral" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                </svg>
            );
        }
        return sortOrder === 'asc' ? (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
        ) : (
            <svg className="ap-icon-sm ap-sort-icon ap-sort-icon--active" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
        );
    };

    // ======================================================================
    // Custom cell rendering
    // ======================================================================
    const renderCell = (row, columnKey) => {
        switch (columnKey) {
            case 'alarm_display': {
                const isOn = row.alarm_display === 'Yes';
                return (
                    <span className={`ws-status ${isOn ? 'ws-status--active' : 'ws-status--inactive'}`}>
                        <span className="ws-status__dot" aria-hidden="true" />
                        {row.alarm_display}
                    </span>
                );
            }
            case 'type_display': {
                const isDefault = row.isDefault;
                return (
                    <span style={{
                        fontSize: 'var(--font-size-xs, 0.75rem)',
                        fontWeight: 500,
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        background: isDefault ? 'var(--color-primary-light, #e0f2fe)' : 'var(--color-bg-tertiary, #f3f4f6)',
                        color: isDefault ? 'var(--color-primary, #0081c7)' : 'var(--color-text-secondary, #6b7280)',
                    }}>
                        {row.type_display}
                    </span>
                );
            }
            case 'device_count': {
                // "All" means every device is covered automatically — nothing to
                // assign, so it stays non-interactive.
                if (row.isDefault) return <span className="ap-cell-text--muted">All</span>;
                return <DevicesPopoverCell deviceObjects={row._deviceObjects} onAssign={onAssignDevices} row={row} />;
            }
            default:
                return <span className="ap-cell-text">{row[columnKey] != null ? row[columnKey] : '\u2014'}</span>;
        }
    };

    // ======================================================================
    // Render
    // ======================================================================
    const hasFilters = search.trim() || Object.values(filters).some((v) => v.length > 0);
    const hasData = (data || []).length > 0;

    return (
        <>
            {/* Create Defaults prompt when no records exist */}
            {!hasData ? (
                <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                    <p className="ss-empty__text">
                        No {typeLabel.toLowerCase()} settings configured for this campus.
                    </p>
                    <button
                        type="button"
                        className="ap-btn ap-btn--primary"
                        style={{ marginTop: 'var(--space-3, 0.75rem)' }}
                        onClick={onCreateDefaults}
                        disabled={saving}
                    >
                        {saving ? 'Creating...' : 'Create Default Settings'}
                    </button>
                </div>
            ) : (
                <>
                    {/* Controls row */}
                    <div className="ap-controls">
                        <div className="ap-controls__left">
                            <div className="ap-search">
                                <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                </svg>
                                <input type="search" placeholder={`Search ${typeLabel.toLowerCase()} settings...`} value={search} onChange={(e) => setSearch(e.target.value)} className="ap-search__input" aria-label={`Search ${typeLabel.toLowerCase()} settings`} />
                            </div>
                        </div>
                        <div className="ap-controls__right">
                            <ColumnVisibilityToggle columns={COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
                            <button type="button" className="ap-btn ap-btn--secondary" onClick={handleExportAll} aria-label={`Export ${typeLabel.toLowerCase()} settings`}>
                                <svg className="ap-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                </svg>
                                Export
                            </button>
                            <button type="button" onClick={onAdd} className="ap-btn ap-btn--primary" aria-label={`Add ${typeLabel.toLowerCase()} setting`}>
                                <svg className="ap-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                                Add Setting
                            </button>
                        </div>
                    </div>

                    {/* Active filter chips */}
                    <ActiveFilterBar filters={filters} columnLabels={COLUMN_LABELS} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />

                    {/* Bulk action bar */}
                    {selectedIds.length > 0 ? (
                        <div className="ap-bulk-bar">
                            <div className="ap-bulk-bar__left">
                                <span className="ap-bulk-bar__count">{selectedIds.length} setting{selectedIds.length !== 1 ? 's' : ''} selected</span>
                                <button type="button" onClick={() => setSelectedIds([])} className="ap-bulk-bar__deselect">Deselect All</button>
                            </div>
                            <div className="ap-bulk-bar__right">
                                <button type="button" onClick={handleBulkExport} className="ap-bulk-btn" aria-label="Export selected">
                                    <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                    </svg>
                                    Export
                                </button>
                                <button type="button" onClick={handleBulkDelete} disabled={saving} className="ap-bulk-btn ap-bulk-btn--danger" aria-label="Delete selected">
                                    <svg className="ap-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                    </svg>
                                    Delete
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {/* Table */}
                    {filteredData.length === 0 ? (
                        <div className="ss-empty" style={{ marginTop: 'var(--space-4)' }}>
                            <p className="ss-empty__text">
                                {hasFilters ? `No ${typeLabel.toLowerCase()} settings match your filters.` : `No ${typeLabel.toLowerCase()} settings configured.`}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="ap-table-wrap">
                                <table className="ap-table">
                                    <thead className="ap-thead">
                                        <tr>
                                            <th scope="col" className="ap-th ap-th--checkbox">
                                                <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = someSelected; }} onChange={handleSelectAll} className="ap-checkbox" aria-label="Select all" />
                                            </th>
                                            <th scope="col" className="ap-th ap-th--actions">Edit</th>
                                            {activeColumns.map((col) => (
                                                <th key={col.key} scope="col" className="ap-th" aria-sort={col.sortable ? ariaSort(sortColumn, sortOrder, col.key) : undefined}>
                                                    <div className="ap-th__content">
                                                        {col.sortable ? (
                                                            <button type="button" onClick={() => handleSort(col.key)} className="ap-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                                                                {col.label}
                                                                {renderSortIcon(col.key)}
                                                            </button>
                                                        ) : <span>{col.label}</span>}
                                                        {col.filterable ? (
                                                            <FilterPopover columnKey={col.key} label={col.label} values={getFilterValues(col.key)} selectedValues={filters[col.key] || []} onChange={handleFilterChange} />
                                                        ) : null}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="ap-tbody">
                                        {paginatedData.map((row) => {
                                            const isSelected = selectedIds.includes(row.control_id);
                                            return (
                                                <tr key={row.control_id} className={`ap-tr${isSelected ? ' is-selected' : ''}`}>
                                                    <td className="ap-td ap-td--checkbox">
                                                        <input type="checkbox" checked={isSelected} onChange={() => handleSelectRow(row.control_id)} className="ap-checkbox" aria-label={`Select ${row.description}`} />
                                                    </td>
                                                    <td className="ap-td ap-td--actions">
                                                        <RowActions row={row} onEdit={onEdit} onAssignDevices={onAssignDevices} onDelete={onDelete} />
                                                    </td>
                                                    {activeColumns.map((col) => (
                                                        <td key={col.key} className="ap-td">
                                                            {renderCell(row, col.key)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <Pagination
                                page={safePage}
                                totalPages={totalPages}
                                total={totalFiltered}
                                perPage={perPage}
                                onPageChange={setPage}
                                onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                                entityLabel={totalFiltered === 1 ? 'setting' : 'settings'}
                                selectId={`${envType}-per-page`}
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default EnvironmentalTable;
